import { type FC, useCallback, useState } from 'react';

import { useParams } from '@tanstack/react-router';

import type { AssetsSegmentSchemaWithId } from '@iconik-io/iconik-api/src/services/assets';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import type { SegmentEntityBase } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import { SHARE_AUTHORIZE_EMAIL } from '../../constants/modals';
import type { SubclipData } from '../../ducks/asset_time_based_metadata/types';
import { openModal } from '../../ducks/modals_manager/slice';
import { selectAsset } from '../../ducks/pages/asset/selectors';
import { selectIsTimeRangedAsset } from '../../ducks/segments/selectors';
import { createSegmentFromFormData } from '../../ducks/segments/thunks';
import { selectIsEmailAuthorizationNeeded } from '../../ducks/share/selectors';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import hasSegmentMetadataViews from '../../utils/has_segment_metadata_views';
import { AssetSegmentFormControlsTop } from '../asset_segment_form_controls_top/asset_segment_form_controls_top';
import selectMergedPermissions from '../permissions_manager/selectors';
import {
  SEGMENT_FORM_TYPE_COMMENT,
  SEGMENT_FORM_TYPE_SUBCLIP,
  SEGMENT_FORM_TYPE_TIME_BASED_METADATA,
  SEGMENT_TYPE_COMMENT,
  SEGMENT_TYPE_GENERIC,
} from '../segment_form/constants';
import { SegmentForm } from '../segment_form/segment_form';
import type { SegmentFormType } from '../segment_form/types';

type Props = {
  formType?: SegmentFormType;
  formClassName?: string;
  assetVersion?: string;
  onSegmentCreated?: (segment: AssetsSegmentSchemaWithId) => void;
  onCancel?: () => void;
};

const segmentFormTypeToSegmentType = (formType: SegmentFormType) => {
  switch (formType) {
    case SEGMENT_FORM_TYPE_COMMENT:
      return SEGMENT_TYPE_COMMENT;
    case SEGMENT_FORM_TYPE_SUBCLIP:
    case SEGMENT_FORM_TYPE_TIME_BASED_METADATA:
      return SEGMENT_TYPE_GENERIC;
    default:
      return SEGMENT_TYPE_COMMENT;
  }
};

export const AssetSegmentForm: FC<Props> = ({
  formClassName,
  formType,
  assetVersion,
  onCancel,
  onSegmentCreated,
}) => {
  const dispatch = useAppDispatch();

  const { store: unifiedPlayerStore } = useBasePlayerContext();

  const { id: assetId, versionId } = useParams({ strict: false });
  const asset = useAppSelector((state) => selectAsset(state, assetId));
  const metadataCategoryViews = useAppSelector(
    (state) => state.metadataCategories.metadataCategoryViews,
  );
  const mergedPermissions = useAppSelector((state) =>
    selectMergedPermissions(state, OBJECT_TYPE_ASSETS, assetId),
  );
  const shareLinkUserEmail = useAppSelector(
    (state) => state.share.shareLinkUserEmail,
  );
  const isEmailAuthorizationNeeded = useAppSelector(
    selectIsEmailAuthorizationNeeded,
  );

  const [segmentFormType, setSegmentFormType] = useState(
    formType ?? SEGMENT_FORM_TYPE_COMMENT,
  );

  const [segment, setSegment] = useState<SegmentEntityBase>({
    segment_type: segmentFormTypeToSegmentType(segmentFormType),
    asset_id: assetId ?? '',
    segment_text: '',
    time_start_milliseconds: 0,
    time_end_milliseconds: 0,
  });

  const [loading, setLoading] = useState(false);

  const [subclipData, setSubclipData] = useState<SubclipData>({
    title: `${asset?.title ?? ''}_subclip`,
    collectionId: asset?.in_collections?.at(0),
  });

  const handleChange = useCallback((data: SegmentEntityBase) => {
    setSegment(data);
  }, []);

  const ensureShareEmailAuthorization = useCallback(() => {
    if (isEmailAuthorizationNeeded) {
      dispatch(openModal({ id: SHARE_AUTHORIZE_EMAIL }));
    }
  }, [isEmailAuthorizationNeeded]);

  const isTimeRangedAsset = useAppSelector((state) =>
    selectIsTimeRangedAsset(state, assetId, versionId),
  );

  const canEditTags =
    isTimeRangedAsset &&
    hasSegmentMetadataViews(metadataCategoryViews) &&
    mergedPermissions?.write;

  const updateSegmentFormType = useCallback(
    (value: SegmentFormType) => {
      setSegmentFormType(value);
      handleChange({
        ...segment,
        segment_type: segmentFormTypeToSegmentType(value),
      });
    },
    [segment, handleChange],
  );

  const handleSubmit = useCallback(
    async (formData: SegmentEntityBase) => {
      if (!asset) {
        return;
      }
      setLoading(true);

      const currentTime = unifiedPlayerStore.playableMedia.$currentTime.get();
      const range = unifiedPlayerStore.segment.$currentSegment.get().range;
      let start;
      let end;
      if (currentTime !== undefined) {
        start = currentTime * 1000;
        end = currentTime * 1000;
      }
      if (range) {
        start = range.start * 1000;
        end = range.end * 1000;
      }

      const segmentType = segment.segment_type;
      return dispatch(
        createSegmentFromFormData(formData, {
          asset,
          start,
          end,
          segmentType,
          segmentFormType,
          shareLinkUserEmail,
          subclipData,
          assetVersion,
        }),
      )
        .then((createdSegment) => {
          unifiedPlayerStore.segment.clearCurrentSegment();
          unifiedPlayerStore.segment.annotation.clearPersonalDraftAnnotation();
          setSegment({
            asset_id: asset.id,
            segment_type: segmentType,
            segment_text: segmentType === SEGMENT_TYPE_COMMENT ? '' : undefined,
          });
          if (createdSegment) {
            onSegmentCreated?.(createdSegment);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [
      asset,
      segment,
      segmentFormType,
      shareLinkUserEmail,
      subclipData,
      assetVersion,
      onSegmentCreated,
    ],
  );

  const handleSubclipTitleChange = useCallback((title: string) => {
    setSubclipData((state) => ({ ...state, title }));
  }, []);

  const handleSubclipCollectionIdChange = useCallback(
    (collectionId: string | undefined) => {
      setSubclipData((state) => ({ ...state, collectionId }));
    },
    [],
  );

  if (!asset) {
    return null;
  }

  return (
    <>
      <AssetSegmentFormControlsTop
        asset={asset}
        canEditTags={canEditTags}
        onEnsureAuthorization={ensureShareEmailAuthorization}
        onFormTypeButtonClick={updateSegmentFormType}
        formType={segmentFormType}
      />
      <SegmentForm
        className={formClassName}
        withColorsPicker
        segment={segment}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        onChange={handleChange}
        isEmailAuthorizationNeeded={isEmailAuthorizationNeeded}
        authorizedEmail={shareLinkUserEmail}
        disabled={loading}
        segmentFormType={segmentFormType}
        subclipData={subclipData}
        onSubclipTitleChange={handleSubclipTitleChange}
        onSubclipCollectionIdChange={handleSubclipCollectionIdChange}
      />
    </>
  );
};
